/**
 *      ▄ █
 *    █ █ █▄▄  LOADBALANCER CLIENT
 *    █ █ ▄ █
 *    ▀     ▀  @copyright Copyright (c) 2025
 */
import { ActivatedRoute, Params, Router } from '@angular/router';
import { AddEditRegionComponent } from '../addEditRegion/add-edit-region.component';
import { BaseComponent } from '../../../../../../core/components';
import { ChangeDetectorRef, Component, ComponentRef, OnInit, ViewChild, ViewContainerRef } from '@angular/core';
import { ErrorResponseModel } from '../../../../../../core/models';
import {
    LanguageService,
    LogService,
    ModalService,
    NavigationService,
    RbacService,
    UtilsService
} from '../../../../../../core/services';
import { Observable } from 'rxjs';
import { RegionsApiService } from '../../../http/regions.api.service';
import { WizardService } from '../../../services/components';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-lb-region-details',
    templateUrl: './region-details.component.html',
    styleUrls: ['./region-details.component.scss']
})
export class RegionDetailsComponent extends BaseComponent implements OnInit {
    /**
     * Reference to embedded component container.
     */
    @ViewChild('embeddedComponentContainer', { read: ViewContainerRef, static: true })
    public embeddedComponentContainer: any;

    /**
     * Store region id.
     */
    public regionId: string;

    /**
     * Store region name.
     */
    public regionName: string = '';

    /**
     * Store table rows.
     */
    public tableRows: any[];

    /**
     * Component embedded flag.
     */
    public embedded: boolean = false;

    /**
     * Store callback.
     */
    public callBack: () => void;

    /**
     * Embedded page open.
     */
    public embeddedPageOpen: boolean = false;

    /**
     * Store permissions lookup.
     */
    public permissionsLookup: any = {};

    /**
     * Store the subnets.
     * @private
     */
    private subnets: string[] = [];

    /**
     * Store component reference.
     * @private
     */
    private componentReference: ComponentRef<any>;

    constructor(
        private cdr: ChangeDetectorRef,
        private languageService: LanguageService,
        private logService: LogService,
        private modalService: ModalService,
        private navigationService: NavigationService,
        private rbacService: RbacService,
        private regionsApiService: RegionsApiService,
        private route: ActivatedRoute,
        private router: Router,
        private utilsService: UtilsService,
        private wizardService: WizardService
    ) {
        super();

        this.setUpRouteSubscribers();
        this.registerButtons();
    }

    public ngOnInit() {
        if (this.embedded) {
            this.setUpPageData();
        }
    }

    /**
     * Go to add/edit subnet page.
     * @param data
     */
    public addEditSubnet(data?: any): void {
        if (this.embedded) {
            this.setUpEmbeddedComponent(data);
        } else {
            const routeData: any = {
                pageType: data ? 'editSubnet' : 'addSubnet',
                subnetToEdit: data?.subnet
            };

            this.router.navigateByUrl(
                this.navigationService.getEnduranceUrl('/network/regions/edit/' + this.regionId),
                { state: routeData }).then().catch();
        }
    }

    /**
     * Return to previous page or close embedded component.
     */
    public goBack(): void {
        if (this.embedded) {
            this.callBack();
        } else {
            this.router.navigate(['..'], { relativeTo: this.route }).then();
        }
    }

    /**
     * Confirm delete subnet.
     * @param data
     * @param index
     */
    public confirmDeleteSubnet(data: any, index: number): void {
        const formattedData: any = this.formatDeleteData(index);
        const message = this.languageService.translate('subnet_delete_message') + data.subnet + '?';
        const confirmationObject = {
            title: 'subnet_delete',
            message: message,
            callBack: this.deleteSubnet.bind(this, formattedData),
            options: { translateText: false }
        };

        this.modalService.confirmDecision(confirmationObject);
    }

    /**
     * Format delete data.
     * @param index
     * @private
     */
    private formatDeleteData(index: number): any {
        const subnets: string[] = [...this.subnets];
        subnets.splice(index, 1);

        return {
            id: this.regionId,
            name: this.regionName,
            subnets: subnets
        };
    }

    /**
     * Delete subnet.
     * @param data
     * @private
     */
    private deleteSubnet(data: any): Observable<any> {
        return new Observable((observer: any): void => {
            this.regionsApiService.editRegion(data)
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: (): void => {
                        this.setUpPageData();
                        observer.next();
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        this.logService.error('RegionDetailsComponent: deleteSubnet',
                            'Delete subnet failed', error.message);
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Set up route subscribers.
     * @private
     */
    private setUpRouteSubscribers(): void {
        this.route.params
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (params: Params): void => {
                    if (params) {
                        this.regionId = params['id'];
                        this.setUpPageData();
                    }
                }
            });
    }

    /**
     * Set up the embedded component.
     * @private
     */
    private setUpEmbeddedComponent(data?: any): void {
        const embeddedComponent = this.embeddedComponentContainer.createComponent(AddEditRegionComponent);
        embeddedComponent.instance.regionId = this.regionId;
        embeddedComponent.instance.regionName = this.regionName;
        embeddedComponent.instance.pageType = data ? 'editSubnet' : 'addSubnet';
        embeddedComponent.instance.subnetToEdit = data?.subnet;
        embeddedComponent.instance.embedded = true;
        embeddedComponent.instance.callBack = this.cancelView.bind(this);

        this.componentReference = embeddedComponent;
        this.embeddedPageOpen = true;
        this.wizardService.disableNavigation = true;
        this.cdr.detectChanges();
    }

    /**
     * Cancel the view when embedded.
     * @private
     */
    private cancelView(): void {
        this.embeddedPageOpen = false;
        this.setUpPageData();
        this.wizardService.disableNavigation = false;
        this.componentReference.destroy();
    }

    /**
     * Set up data for page.
     * @private
     */
    private setUpPageData(): void {
        if (this.regionId) {
            this.regionsApiService.getRegion(this.regionId)
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: (data: any): void => {
                        if (data) {
                            this.regionName = data.name;
                            this.subnets = data.subnets;
                            this.formatDataForTable(data);
                        }
                    },
                    error: (error: ErrorResponseModel): void => {
                        this.logService.error('RegionDetailsComponent: setUpPageData ',
                            'Failed to get subnets', error.message);
                    }
                });
        } else {
            this.logService.error('RegionDetailsComponent: setUpPageData() ', 'Id undefined');
        }
    }

    /**
     * Format data to display in table.
     * @param data
     * @private
     */
    private formatDataForTable(data: any): void {
        const rows: any[] = [];
        for (const subnet of data.subnets) {
            rows.push({
                id: this.utilsService.generateUniqueId(subnet),
                subnet: subnet
            });
        }
        this.tableRows = rows;
    }

    /**
     * Register button permissions.
     * @private
     */
    private registerButtons(): void {
        const buttonReference = {
            'region-edit-subnet': [{ path: '/regions/:id', methods: ['PUT'] }],
            'region-delete-subnet': [{ path: '/regions/:id', methods: ['DELETE'] }]
        };

        this.permissionsLookup = this.rbacService.getPermissionsLookup(buttonReference);
    }
}
